/**
 * Stone Skin — Plan 4a Task 9.1 + 9.2
 *
 * Self-targeted buff. Wraps the caster in a stone shield that blocks exactly one incoming
 * hostile non-self effect, including damage, theft, freeze, curse, hostile relocation,
 * hostile duel triggers, Soul Anchor/hazard effects, Orbital Cannon, and Shadow Stalker.
 *
 * Cast behaviour:
 *   - targetType: 'self' — caster always targets themselves.
 *   - blockedByStoneSkin: false — applying Stone Skin to yourself cannot be blocked by Stone Skin.
 *   - cast() calls applyStatusEffect(state, casterId, 'stoneSkin', 0, 'item_stone_skin')
 *     which sets stoneSkin.active=true and emits status_effect_applied.
 *   - Emits item_cast after applying the effect.
 *   - Rule 2 (Task 9.2): Not stackable. If player already has active Stone Skin, the second
 *     cast fails (returns {success: false}) and no new status_effect_applied is emitted.
 *     The attempt is still logged via item_cast event.
 *
 * Self-registers via addItem(stoneSkin) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { applyStatusEffect, hasStoneSkin } from './status-effects';
import { addItem } from './registry';

export const stoneSkin: Item = {
  id: 'stone_skin',
  name: 'Stone Skin',
  description:
    'Grants a one-use stone shield that blocks the next incoming hostile effect against you.',
  targetType: 'self',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: false,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;

    // Step 1: Validate caster
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Stone Skin: caster not found: ${casterId}`);

    // Step 2: Rule 2 — Not stackable. If already active, reject the cast.
    if (hasStoneSkin(state, casterId)) {
      // Emit item_cast to track the attempt, but return failure (no status_effect_applied)
      appendEvent(state, {
        type: 'item_cast',
        playerId: casterId,
        casterId,
        itemId: 'stone_skin',
      });
      return { success: false };
    }

    // Step 3: Apply stoneSkin status effect (sets active=true, emits status_effect_applied)
    applyStatusEffect(state, casterId, 'stoneSkin', 0, 'item_stone_skin');

    // Step 4: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'stone_skin',
    });

    return { success: true };
  },
};

// Self-register at module load
addItem(stoneSkin);
